<template>
  <div class="route-design-map-page">
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
    ></v-progress-linear>

    <v-layout v-if="!loading" row class="fill-height">
      <!-- ===== LEFT SIDEBAR ===== -->
      <div class="sidebar elevation-3">
        <!-- Route Header -->
        <div class="sidebar-header">
          <div class="d-flex align-center px-3 pt-3 mt-1 pb-2">
            <button
              type="button"
              class="back-btn mr-3"
              @click="goBack"
              title="Back to route list"
            >
              <v-icon small color="white">mdi-arrow-left</v-icon>
              <span>Back</span>
            </button>
            <div class="flex-grow-1" style="min-width:0">
              <div
                class="subheading font-weight-bold white--text text-truncate"
                style="line-height:1.35"
              >
                {{ routeDesign && routeDesign.name }}
              </div>
              <div
                class="caption white--text"
                style="opacity:.8;line-height:1.3"
              >
                {{ (routeDesign && routeDesign.seasonName) || "" }}
                <span v-if="routeDesign && routeDesign.regID">
                  · {{ routeDesign.regID }}</span
                >
              </div>
            </div>
            <div
              class="status-pill ml-2"
              :class="
                'status-pill--' +
                  ((routeDesign && routeDesign.status) || 'Draft').toLowerCase()
              "
            >
              {{ routeDesign && routeDesign.status }}
            </div>
          </div>

          <div
            class="d-flex flex-wrap px-3 pb-3 pt-2"
            style="gap:5px;margin-top:2px"
          >
            <span v-if="routeDesign && routeDesign.building" class="header-tag">
              <v-icon x-small color="white">mdi-office-building</v-icon>
              {{ routeDesign.building }}
            </span>
            <span v-if="routeDesign && routeDesign.branch" class="header-tag">
              <v-icon x-small color="white">mdi-source-branch</v-icon>
              {{ routeDesign.branch }}
            </span>
            <span v-if="routeDesign && routeDesign.grades" class="header-tag">
              <v-icon x-small color="white">mdi-school</v-icon>
              {{ routeDesign.grades }}
            </span>
          </div>
        </div>

        <!-- Student pool count -->
        <div class="px-3 py-2 grey lighten-4 caption grey--text text--darken-2">
          Student pool: {{ poolStudents.length }} students &nbsp;|&nbsp;
          Assigned: {{ totalAssigned }}
        </div>

        <!-- Buses section -->
        <div class="sidebar-buses pa-2">
          <div class="d-flex align-center px-1 mb-2">
            <div class="subheading font-weight-medium">Buses</div>
            <v-spacer></v-spacer>
            <v-btn small color="success" @click="openAddBusDialog">
              <v-icon left small>mdi-plus</v-icon>Add Bus
            </v-btn>
          </div>

          <div
            v-if="!buses.length"
            class="text-xs-center grey--text pa-4 caption"
          >
            No buses yet. Add the first bus to start designing.
          </div>

          <v-card
            v-for="bus in buses"
            :key="bus.id"
            class="bus-card mb-2"
            :class="{ 'bus-card--active': activeBusId === bus.id }"
            :style="
              activeBusId === bus.id
                ? 'border-left: 4px solid ' + busColor(bus) + ' !important'
                : 'border-left: 4px solid transparent'
            "
            @click="selectBus(bus)"
          >
            <div class="pa-2 d-flex align-center" style="min-height:48px">
              <span
                class="bus-color-dot mr-2 flex-shrink-0"
                :style="{ background: busColor(bus) }"
                title="Bus color"
              ></span>
              <div class="flex-grow-1" style="overflow:hidden">
                <div class="d-flex align-center">
                  <span
                    class="body-2 font-weight-medium"
                    style="white-space:nowrap"
                    >{{ bus.busNumber }}</span
                  >
                  <v-icon
                    v-if="bus.isCompleted"
                    small
                    color="green"
                    class="ml-1"
                    style="flex-shrink:0"
                    title="Completed"
                    >mdi-check-circle</v-icon
                  >
                </div>
                <div
                  class="caption grey--text d-flex align-center"
                  style="line-height:1.3"
                >
                  <span>{{ busStudentCount(bus.id) }} students</span>
                  <span
                    v-if="bus.isCompleted"
                    class="green--text ml-1"
                    style="white-space:nowrap"
                    >· saved</span
                  >
                  <span
                    v-else-if="activeBusId === bus.id"
                    class="success--text ml-1"
                    style="white-space:nowrap"
                    >· working…</span
                  >
                </div>
              </div>
              <div
                class="d-flex align-center ml-auto flex-shrink-0"
                style="gap:2px"
              >
                <button
                  type="button"
                  class="bus-action-btn"
                  @click.stop="editBusNumber(bus)"
                  title="Rename bus"
                >
                  <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                </button>
                <button
                  type="button"
                  class="bus-action-btn"
                  @click.stop="confirmDeleteBus(bus)"
                  title="Delete bus"
                >
                  <v-icon small color="error">mdi-delete</v-icon>
                </button>
              </div>
            </div>
          </v-card>
        </div>

        <!-- Selected Students Panel -->
        <div v-if="activeBusId" class="sidebar-students">
          <div
            class="d-flex align-center px-3 py-2 grey lighten-3 selected-students-header"
            style="cursor:pointer"
            @click="studentsExpanded = !studentsExpanded"
          >
            <div class="body-2 font-weight-medium">
              Selected Students
              <v-chip x-small color="success" text-color="white" class="ml-1">{{
                activeBusStudents.length
              }}</v-chip>
            </div>
            <v-spacer></v-spacer>
            <v-icon small class="grey--text">{{
              studentsExpanded ? "mdi-chevron-up" : "mdi-chevron-down"
            }}</v-icon>
          </div>
          <div v-if="studentsExpanded" class="selected-students-list">
            <div
              v-if="!activeBusStudents.length"
              class="caption grey--text pa-3 text-xs-center"
            >
              No students selected for this bus yet
            </div>
            <div
              v-for="s in activeBusStudents"
              :key="s.studentID"
              class="student-row px-3 py-1"
              style="display:grid;grid-template-columns:1fr auto auto;align-items:center;gap:0 6px"
            >
              <div style="min-width:0">
                <div class="caption font-weight-medium text-truncate">
                  {{ s.fullName }}
                </div>
                <div class="caption grey--text text-truncate">
                  {{
                    s.busAddress1 ||
                      (s.house && s.street ? s.house + " " + s.street : "") ||
                      "—"
                  }}
                </div>
              </div>
              <span
                class="caption grey--text text--darken-1"
                style="white-space:nowrap;text-align:right"
                >{{ s.class }}-{{ s.section }}</span
              >
              <v-btn
                icon
                x-small
                flat
                color="error"
                @click="removeStudent(s.studentID)"
                title="Remove from bus"
                style="margin:0"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Sidebar footer actions -->
        <div class="sidebar-footer pa-2">
          <v-divider class="mb-2"></v-divider>
          <div v-if="activeBusId" class="mb-1">
            <v-btn color="success" block :loading="savingBus" @click="saveBus">
              <v-icon left>{{
                nextIncompleteBus
                  ? "mdi-arrow-right-circle"
                  : "mdi-check-circle"
              }}</v-icon>
              {{ saveBusLabel }}
            </v-btn>
          </div>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                color="success"
                outline
                block
                :loading="savingFinal"
                @click="saveFinal"
                v-on="on"
              >
                <v-icon left>mdi-content-save-all</v-icon>
                Save Route Status
              </v-btn>
            </template>
            <span
              >Saves the overall route status (Partial / Complete). Use this
              after all buses are done.</span
            >
          </v-tooltip>
          <v-btn color="success" outline block class="mt-2" @click="exportCSV">
            <v-icon left>mdi-file-excel</v-icon>
            Export All to CSV
          </v-btn>
        </div>
      </div>

      <!-- ===== MAIN MAP AREA ===== -->
      <div class="map-area">
        <!-- Toolbar row 1: bus status + mode + filters -->
        <div class="map-toolbar elevation-1 px-2 py-1 d-flex align-center">
          <!-- Active bus indicator -->
          <div class="d-flex align-center mr-2" style="min-width:0">
            <span v-if="activeBusId" class="d-flex align-center">
              <span
                class="bus-color-dot mr-1"
                :style="{ background: activeBusColor }"
              ></span>
              <span
                class="caption font-weight-medium"
                style="white-space:nowrap"
                >{{ activeBusNumber }}</span
              >
            </span>
            <span v-else class="caption grey--text" style="white-space:nowrap">
              ← Select a bus
            </span>
          </div>

          <v-divider vertical class="mx-2" style="height:24px"></v-divider>

          <!-- Drawing mode toggle -->
          <div
            class="draw-toggle-group"
            :class="{ 'draw-toggle-group--disabled': !activeBusId }"
            :title="!activeBusId ? 'Select a bus first to enable drawing' : ''"
          >
            <button
              type="button"
              class="draw-toggle-btn"
              :class="{ 'draw-toggle-btn--active': drawingMode === 'LINE' }"
              @click="drawingMode = 'LINE'"
              title="Click on map to trace a street → double-click to finish"
            >
              <v-icon small>mdi-gesture</v-icon> Line
            </button>
            <button
              type="button"
              class="draw-toggle-btn"
              :class="{
                'draw-toggle-btn--active': drawingMode === 'RADIUS_LINE',
              }"
              @click="drawingMode = 'RADIUS_LINE'"
              title="Trace an avenue → selects students within radius on both sides"
            >
              <v-icon small>mdi-map-marker-radius</v-icon> Radius
            </button>
            <button
              type="button"
              class="draw-toggle-btn"
              :class="{ 'draw-toggle-btn--active': drawingMode === 'BOX' }"
              @click="drawingMode = 'BOX'"
              title="Hold Shift and drag on the map"
            >
              <v-icon small>mdi-select</v-icon> Box
            </button>
          </div>

          <!-- Radius input — inline inside toolbar when in RADIUS_LINE mode -->
          <div
            v-if="activeBusId && drawingMode === 'RADIUS_LINE'"
            class="radius-inline-input ml-2"
          >
            <input
              v-model.number="radiusFeet"
              type="number"
              min="50"
              max="5000"
              class="radius-inline-input__field"
              title="Selection radius in feet"
            />
            <span class="radius-inline-input__unit">ft</span>
          </div>

          <!-- Drawing in progress -->
          <div v-if="isDrawingLine" class="d-flex align-center ml-2">
            <v-icon small color="success" class="mr-1">mdi-record</v-icon>
            <span class="caption success--text mr-2"
              >Drawing… dbl-click to finish</span
            >
            <v-btn x-small flat color="error" @click="cancelDrawing">
              <v-icon x-small left>mdi-close</v-icon>Cancel
            </v-btn>
          </div>

          <v-spacer></v-spacer>

          <v-btn
            small
            outline
            class="mr-1"
            @click="filterPanelOpen = !filterPanelOpen"
          >
            <v-icon small left>mdi-filter</v-icon>
            Filters
            <v-chip
              v-if="activeFilterCount"
              x-small
              color="success"
              text-color="white"
              class="ml-1"
              >{{ activeFilterCount }}</v-chip
            >
          </v-btn>
          <v-btn
            small
            outline
            @click="clearAllSelections"
            title="Clear all selections for active bus"
          >
            <v-icon small left>mdi-eraser</v-icon>
            Clear Bus
          </v-btn>
        </div>

        <!-- Instruction hint strip -->
        <div v-if="activeBusId" class="instruction-strip">
          <!-- Drawing in progress -->
          <div
            v-if="isDrawingLine"
            class="instruction-strip__row instruction-strip__row--active"
          >
            <v-icon small color="success" class="instruction-strip__icon"
              >mdi-record-circle</v-icon
            >
            <div>
              <div class="instruction-strip__main success--text">
                Drawing in progress
              </div>
              <div class="instruction-strip__sub">
                Keep clicking to add points along the street —
                <strong>double-click</strong> to finish and select students.
              </div>
            </div>
          </div>

          <!-- Line mode -->
          <div
            v-else-if="drawingMode === 'LINE'"
            class="instruction-strip__row"
          >
            <v-icon small color="green darken-1" class="instruction-strip__icon"
              >mdi-gesture</v-icon
            >
            <div>
              <div class="instruction-strip__main">Line mode</div>
              <div class="instruction-strip__sub">
                Click on the map to trace a street, then
                <strong>double-click</strong> to finish. Students within ~100 ft
                of the line are selected.
              </div>
            </div>
          </div>

          <!-- Radius Line mode -->
          <div
            v-else-if="drawingMode === 'RADIUS_LINE'"
            class="instruction-strip__row"
          >
            <v-icon small color="green darken-1" class="instruction-strip__icon"
              >mdi-map-marker-radius</v-icon
            >
            <div>
              <div class="instruction-strip__main">
                Radius Line mode &mdash; {{ radiusFeet }} ft radius
              </div>
              <div class="instruction-strip__sub">
                Click along an avenue, then <strong>double-click</strong> to
                finish. Students within {{ radiusFeet }} ft on both sides are
                selected.
              </div>
            </div>
          </div>

          <!-- Box mode -->
          <div v-else-if="drawingMode === 'BOX'" class="instruction-strip__row">
            <v-icon small color="green darken-1" class="instruction-strip__icon"
              >mdi-selection</v-icon
            >
            <div>
              <div class="instruction-strip__main">Box mode</div>
              <div class="instruction-strip__sub">
                Hold <kbd>Shift</kbd> and drag on the map to draw a rectangle —
                all students inside are selected.
              </div>
            </div>
          </div>

          <!-- Tip row (always shown when not drawing) -->
          <div v-if="!isDrawingLine" class="instruction-strip__tip">
            Click any student dot on the map to select or deselect individually.
          </div>
        </div>

        <!-- Filter panel (collapsible) -->
        <v-slide-y-transition>
          <div v-show="filterPanelOpen" class="filter-panel elevation-1 pa-3">
            <v-layout row wrap>
              <v-flex xs6 sm3 class="pr-2">
                <v-autocomplete
                  v-model="filters.building"
                  :items="buildingOptions"
                  label="Building"
                  dense
                  outlined
                  multiple
                  clearable
                  hide-details
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0" small>{{ item }}</v-chip>
                    <span v-if="index === 1" class="grey--text caption"
                      >(+{{ filters.building.length - 1 }})</span
                    >
                  </template>
                </v-autocomplete>
              </v-flex>
              <v-flex xs6 sm3 class="pr-2">
                <v-autocomplete
                  v-model="filters.branch"
                  :items="branchOptions"
                  label="Branch"
                  dense
                  outlined
                  multiple
                  clearable
                  hide-details
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0" small>{{ item }}</v-chip>
                    <span v-if="index === 1" class="grey--text caption"
                      >(+{{ filters.branch.length - 1 }})</span
                    >
                  </template>
                </v-autocomplete>
              </v-flex>
              <v-flex xs6 sm3 class="pr-2">
                <v-autocomplete
                  v-model="filters.class"
                  :items="classOptions"
                  label="Grade"
                  dense
                  outlined
                  multiple
                  clearable
                  hide-details
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0" small>{{ item }}</v-chip>
                    <span v-if="index === 1" class="grey--text caption"
                      >(+{{ filters.class.length - 1 }})</span
                    >
                  </template>
                </v-autocomplete>
              </v-flex>
              <v-flex xs6 sm3>
                <v-autocomplete
                  v-model="filters.section"
                  :items="sectionOptions"
                  label="Section"
                  dense
                  outlined
                  multiple
                  clearable
                  hide-details
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index === 0" small>{{ item }}</v-chip>
                    <span v-if="index === 1" class="grey--text caption"
                      >(+{{ filters.section.length - 1 }})</span
                    >
                  </template>
                </v-autocomplete>
              </v-flex>
            </v-layout>
          </div>
        </v-slide-y-transition>

        <!-- Map -->
        <div class="map-container" ref="mapContainer">
          <l-map
            ref="map"
            :zoom="zoom"
            :center="center"
            @ready="onMapReady"
            class="the-map"
          >
            <l-tile-layer
              :url="tileUrl"
              :attribution="attribution"
            ></l-tile-layer>

            <l-marker
              v-for="loc in visibleLocations"
              :key="loc.id"
              :lat-lng="[loc.lat, loc.lng]"
              :icon="markerIcon(loc)"
              @click="onMarkerClick(loc)"
            >
              <l-popup>
                <div style="min-width:180px">
                  <strong>{{ loc.name }}</strong
                  ><br />
                  <span class="grey--text" style="font-size:0.85em"
                    >{{ loc.residents.length }} students</span
                  >
                  <div
                    v-for="r in loc.residents"
                    :key="r.uniqueId"
                    style="font-size:0.85em;margin-top:4px"
                  >
                    {{ r.fullName }}
                    <span class="grey--text"
                      >· {{ r.class }}-{{ r.section }}</span
                    >
                    <span
                      v-if="studentBusId(r.studentID)"
                      :style="{
                        color: busColorById(studentBusId(r.studentID)),
                      }"
                      class="ml-1"
                    >
                      ● {{ busNumberById(studentBusId(r.studentID)) }}
                    </span>
                  </div>
                </div>
              </l-popup>
            </l-marker>
          </l-map>
        </div>
      </div>
    </v-layout>

    <!-- ADD BUS DIALOG -->
    <v-dialog v-model="addBusDialog" max-width="360">
      <v-card>
        <v-card-title class="title pt-3 pb-1">Add Bus</v-card-title>
        <v-card-text class="pt-2 pb-1">
          <v-text-field
            v-model="newBusNumber"
            label="Bus Number / Name"
            outlined
            dense
            autofocus
            @keyup.enter="doAddBus"
          ></v-text-field>
          <div class="caption grey--text mt-n2">
            Leave blank to auto-name (Bus 1, Bus 2, …)
          </div>
        </v-card-text>
        <v-card-actions class="pt-1 pb-3 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="addBusDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" :loading="addingBus" @click="doAddBus"
            >Add</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- RENAME BUS DIALOG -->
    <v-dialog v-model="renameBusDialog" max-width="380">
      <v-card>
        <v-card-title class="headline">Rename Bus</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="renameBusNumber"
            label="Bus Number / Name"
            outlined
            dense
            autofocus
            @keyup.enter="doRenameBus"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pb-3 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="renameBusDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" :loading="renamingBus" @click="doRenameBus"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DELETE BUS DIALOG -->
    <v-dialog v-model="deleteBusDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Delete Bus?</v-card-title>
        <v-card-text>
          Delete
          <strong>{{ deleteBusTarget && deleteBusTarget.busNumber }}</strong
          >? This will remove
          {{ busStudentCount(deleteBusTarget && deleteBusTarget.id) }} student
          assignments and all drawings for this bus.
        </v-card-text>
        <v-card-actions class="pb-3 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="deleteBusDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="error" :loading="deletingBus" @click="doDeleteBus"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- FILTER CONFLICT DIALOG -->
    <v-dialog v-model="filterConflictDialog" max-width="460" persistent>
      <v-card>
        <v-card-title class="headline">Filter Change Warning</v-card-title>
        <v-card-text>
          Changing filters will hide
          <strong>{{ filterConflictCount }}</strong> student(s) that are already
          selected for <strong>{{ activeBusNumber }}</strong
          >. Their assignments won't be lost — they'll still be saved — but they
          won't be visible on the map.
        </v-card-text>
        <v-card-actions class="pb-3 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelFilterChange"
            >Keep current filters</v-btn
          >
          <v-btn color="success" outline @click="applyFilterChange"
            >Apply filters anyway</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- FINAL SAVE DIALOG -->
    <v-dialog v-model="finalSaveDialog" max-width="440">
      <v-card>
        <v-card-title class="headline">Save Route Design</v-card-title>
        <v-card-text>
          <div class="mb-3">
            <strong>{{ completedBusCount }}</strong> of
            <strong>{{ buses.length }}</strong> buses are marked complete.
          </div>
          <v-select
            v-model="finalStatus"
            :items="['Partial', 'Complete']"
            label="Set Status"
            outlined
            dense
          ></v-select>
        </v-card-text>
        <v-card-actions class="pb-3 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="finalSaveDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" :loading="savingFinal" @click="doFinalSave"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" top :timeout="3000">
      {{ snackbarText }}
      <v-btn dark flat @click="snackbar = false">Close</v-btn>
    </v-snackbar>

    <v-alert
      :value="!!errorMessage"
      type="error"
      @input="errorMessage = ''"
      class="ma-3"
    >
      {{ errorMessage }}
    </v-alert>
  </div>
</template>

<script>
import { LMap, LTileLayer, LMarker, LPopup } from "vue2-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const BUS_COLORS = [
  "#1565C0",
  "#2E7D32",
  "#E65100",
  "#6A1B9A",
  "#00695C",
  "#AD1457",
  "#F57F17",
  "#0277BD",
  "#4527A0",
  "#37474F",
  "#BF360C",
  "#1B5E20",
];
const FEET_TO_METERS = 0.3048;
const LINE_DEFAULT_RADIUS_FEET = 100;

function createCircleIcon(size, color, opacity) {
  const r = size / 2;
  const sw = 1.5;
  const svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
    <circle cx="${r}" cy="${r}" r="${r -
    sw}" fill="${color}" fill-opacity="${opacity}" stroke="#fff" stroke-width="${sw}"/>
  </svg>`;
  return L.divIcon({
    html: svg,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    className: "custom-circle-marker",
  });
}

function distanceToSegment(p, a, b) {
  const dx = b.lat - a.lat;
  const dy = b.lng - a.lng;
  if (dx === 0 && dy === 0) {
    return p.distanceTo(L.latLng(a.lat, a.lng));
  }
  const t = Math.max(
    0,
    Math.min(
      1,
      ((p.lat - a.lat) * dx + (p.lng - a.lng) * dy) / (dx * dx + dy * dy)
    )
  );
  return p.distanceTo(L.latLng(a.lat + t * dx, a.lng + t * dy));
}

function distanceToPolyline(lat, lng, points) {
  const p = L.latLng(lat, lng);
  let min = Infinity;
  for (let i = 0; i < points.length - 1; i++) {
    const d = distanceToSegment(p, points[i], points[i + 1]);
    if (d < min) min = d;
  }
  return min;
}

export default {
  name: "RouteDesignMap",
  components: { LMap, LTileLayer, LMarker, LPopup },
  data() {
    return {
      loading: true,
      routeDesign: null,
      buses: [],
      busStudentMap: {},
      busAssignmentSourceMap: {},
      busDrawingMap: {},

      students: [],
      addressInfos: [],

      activeBusId: null,

      drawingMode: "LINE",
      radiusFeet: 500,

      isDrawingLine: false,
      drawingPoints: [],
      currentPolyline: null,
      previewPolyline: null,

      rectangle: null,
      startLatLng: null,

      map: null,
      drawnLayers: {},

      filters: { building: [], branch: [], class: [], section: [] },
      filterPanelOpen: false,
      buildingOptions: [],
      branchOptions: [],
      classOptions: [],
      sectionOptions: [],

      pendingFilters: null,
      filterConflictDialog: false,
      filterConflictCount: 0,

      addBusDialog: false,
      newBusNumber: "",
      addingBus: false,

      renameBusDialog: false,
      renameBusTarget: null,
      renameBusNumber: "",
      renamingBus: false,

      deleteBusDialog: false,
      deleteBusTarget: null,
      deletingBus: false,

      savingBus: false,
      finalSaveDialog: false,
      finalStatus: "Partial",
      savingFinal: false,

      zoom: 13,
      center: [40.7128, -73.95],
      tileUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: "&copy; OpenStreetMap contributors",

      studentsExpanded: true,
      _clickTimer: null,

      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      errorMessage: "",
    };
  },
  computed: {
    statusChipColor() {
      if (!this.routeDesign) return "blue-grey";
      if (this.routeDesign.status === "Complete") return "green darken-1";
      if (this.routeDesign.status === "Partial") return "orange darken-2";
      return "blue-grey";
    },
    poolStudents() {
      if (!this.routeDesign) return this.students;
      return this.students.filter((s) => {
        const rd = this.routeDesign;
        if (rd.building && s.building !== rd.building) return false;
        if (rd.branch && s.branch !== rd.branch) return false;
        if (rd.grades) {
          const gradeList = rd.grades.split(",").map((g) => g.trim());
          if (gradeList.length && !gradeList.includes(s.class)) return false;
        }
        return true;
      });
    },
    filteredStudents() {
      return this.poolStudents.filter((s) => {
        return (
          (this.filters.building.length === 0 ||
            this.filters.building.includes(s.building)) &&
          (this.filters.branch.length === 0 ||
            this.filters.branch.includes(s.branch)) &&
          (this.filters.class.length === 0 ||
            this.filters.class.includes(s.class)) &&
          (this.filters.section.length === 0 ||
            this.filters.section.includes(s.section))
        );
      });
    },
    visibleLocations() {
      const grouped = {};
      this.filteredStudents.forEach((s, i) => {
        const key = (s.busHouse || "") + "|" + (s.busStreet || "");
        if (!grouped[key]) {
          grouped[key] = {
            house: s.busHouse,
            street: s.busStreet,
            residents: [],
            building: s.building,
          };
        }
        grouped[key].residents.push(s);
      });

      // Build set of student IDs assigned to OTHER buses (not the active bus)
      const otherBusStudentIds = new Set();
      if (this.activeBusId) {
        Object.keys(this.busStudentMap).forEach((busId) => {
          if (parseInt(busId) !== this.activeBusId) {
            this.busStudentMap[busId].forEach((id) =>
              otherBusStudentIds.add(id)
            );
          }
        });
      }

      const result = [];
      Object.values(grouped).forEach((g) => {
        const info = this.addressInfos.find(
          (a) => a.house === g.house && a.street === g.street
        );
        if (!info) return;

        // Filter out residents already on other buses
        const availableResidents = this.activeBusId
          ? g.residents.filter((r) => !otherBusStudentIds.has(r.studentID))
          : g.residents;

        // Skip the location entirely if all residents are on other buses
        if (!availableResidents.length) return;

        result.push({
          id: info.id,
          name: (g.house || "") + " " + (g.street || ""),
          lat: parseFloat(info.busAddressLatitude),
          lng: parseFloat(info.busAddressLongitude),
          residents: availableResidents,
          building: g.building,
        });
      });
      return result;
    },
    activeFilterCount() {
      return (
        this.filters.building.length +
        this.filters.branch.length +
        this.filters.class.length +
        this.filters.section.length
      );
    },
    activeBusColor() {
      const bus = this.buses.find((b) => b.id === this.activeBusId);
      return bus ? this.busColor(bus) : "#999";
    },
    activeBusNumber() {
      const bus = this.buses.find((b) => b.id === this.activeBusId);
      return bus ? bus.busNumber : "";
    },
    totalAssigned() {
      let total = 0;
      Object.values(this.busStudentMap).forEach((ids) => {
        total += ids.length;
      });
      return total;
    },
    completedBusCount() {
      return this.buses.filter((b) => b.isCompleted).length;
    },
    nextIncompleteBus() {
      return (
        this.buses.find((b) => !b.isCompleted && b.id !== this.activeBusId) ||
        null
      );
    },
    saveBusLabel() {
      if (this.nextIncompleteBus)
        return "Save & Go to " + this.nextIncompleteBus.busNumber;
      return "Save & Mark Complete";
    },
    activeBusStudents() {
      if (!this.activeBusId) return [];
      const ids = this.busStudentMap[this.activeBusId] || [];
      return ids
        .map((id) => this.students.find((s) => s.studentID === id))
        .filter(Boolean);
    },
  },
  watch: {
    drawingMode() {
      this.updateMapCursor();
    },
    activeBusId() {
      this.updateMapCursor();
    },
    filters: {
      handler(newVal) {
        if (!this.activeBusId) return;
        const currentIds = this.busStudentMap[this.activeBusId] || [];
        if (!currentIds.length) return;
        const filteredIds = this.filteredStudents.map((s) => s.studentID);
        const hidden = currentIds.filter((id) => !filteredIds.includes(id));
        if (hidden.length) {
          this.filterConflictCount = hidden.length;
          this.pendingFilters = JSON.parse(JSON.stringify(newVal));
          this.filterConflictDialog = true;
        }
      },
      deep: true,
    },
  },
  async created() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      try {
        this.loading = true;
        const id = this.$route.params.id;
        const [designData, students, addressInfos, seasons] = await Promise.all(
          [
            this.$http.get("api/routeDesign/getRouteDesign?id=" + id),
            this.$http.get("api/geoStudents/getStudents"),
            this.$http.get("api/geoStudents/getAddressInfos"),
            this.$http.get("api/route/getRouteSeasons"),
          ]
        );

        const design = designData.design || {};
        // Resolve seasonName from the seasons list (use loose == to handle int/string mismatch)
        const matchedSeason = (seasons || []).find(
          (s) => s.id == design.seasonID || s.ID == design.seasonID
        );
        const seasonName = matchedSeason
          ? matchedSeason.season ||
            matchedSeason.Season ||
            matchedSeason.seasonName ||
            ""
          : "";
        this.routeDesign = { ...design, seasonName };
        this.buses = (designData.buses || []).map((b, i) => ({
          ...b,
          _colorIndex: i,
        }));
        this.addressInfos = addressInfos || [];

        this.students = (students || []).map((s, i) => ({
          ...s,
          fullName:
            ((s.hebName || "") + " " + (s.lastNameH || "")).trim() || s.engName,
          uniqueId: ((s.studentID || "").trim() || "u") + "-" + i,
        }));

        // Rebuild busStudentMap from saved data
        this.busStudentMap = {};
        this.busAssignmentSourceMap = {};
        (designData.students || []).forEach((bs) => {
          if (!this.busStudentMap[bs.routeDesignBusID]) {
            this.busStudentMap[bs.routeDesignBusID] = [];
            this.busAssignmentSourceMap[bs.routeDesignBusID] = {};
          }
          this.busStudentMap[bs.routeDesignBusID].push(bs.studentID);
          this.busAssignmentSourceMap[bs.routeDesignBusID][bs.studentID] =
            bs.assignmentSource;
        });

        // Rebuild busDrawingMap
        this.busDrawingMap = {};
        (designData.drawings || []).forEach((d) => {
          if (!this.busDrawingMap[d.routeDesignBusID]) {
            this.busDrawingMap[d.routeDesignBusID] = [];
          }
          this.busDrawingMap[d.routeDesignBusID].push(d);
        });

        // Build filter options from pool students
        const pool = this.students;
        const ns = (a, b) =>
          a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
        this.buildingOptions = [
          ...new Set(pool.map((s) => s.building).filter(Boolean)),
        ].sort(ns);
        this.branchOptions = [
          ...new Set(pool.map((s) => s.branch).filter(Boolean)),
        ].sort(ns);
        this.classOptions = [
          ...new Set(pool.map((s) => s.class).filter(Boolean)),
        ].sort(ns);
        this.sectionOptions = [
          ...new Set(pool.map((s) => s.section).filter(Boolean)),
        ].sort(ns);

        // Set active bus to first incomplete bus if any
        const firstIncomplete = this.buses.find((b) => !b.isCompleted);
        if (firstIncomplete) this.activeBusId = firstIncomplete.id;
      } catch (e) {
        console.error(e);
        this.errorMessage = "Failed to load route design data";
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.push({ name: "RouteDesigns" });
    },

    // ── Bus color ──
    busColor(bus) {
      return BUS_COLORS[bus._colorIndex % BUS_COLORS.length];
    },
    busColorById(busId) {
      const bus = this.buses.find((b) => b.id === busId);
      return bus ? this.busColor(bus) : "#999";
    },
    busNumberById(busId) {
      const bus = this.buses.find((b) => b.id === busId);
      return bus ? bus.busNumber : "";
    },
    busStudentCount(busId) {
      return busId && this.busStudentMap[busId]
        ? this.busStudentMap[busId].length
        : 0;
    },
    studentBusId(studentID) {
      for (const busId in this.busStudentMap) {
        if (this.busStudentMap[busId].includes(studentID))
          return parseInt(busId);
      }
      return null;
    },

    // ── Marker icons ──
    markerIcon(loc) {
      if (!loc.residents || !loc.residents.length)
        return createCircleIcon(10, "#9E9E9E", 0.8);

      // Check if any resident is on the active bus
      if (this.activeBusId) {
        const activeIds = this.busStudentMap[this.activeBusId] || [];
        const hasActive = loc.residents.some((r) =>
          activeIds.includes(r.studentID)
        );
        if (hasActive) return createCircleIcon(14, this.activeBusColor, 1);
      }

      // Check if any resident is on any bus
      for (const b of this.buses) {
        const ids = this.busStudentMap[b.id] || [];
        if (loc.residents.some((r) => ids.includes(r.studentID))) {
          return createCircleIcon(11, this.busColor(b), 0.85);
        }
      }

      return createCircleIcon(9, "#B71C1C", 0.65);
    },

    // ── Select bus ──
    selectBus(bus) {
      if (this.isDrawingLine) {
        this.cancelDrawing();
      }
      this.activeBusId = bus.id;
      this.$nextTick(() => this.updateDrawingLayerOpacity());
    },
    updateDrawingLayerOpacity() {
      this.buses.forEach((b) => {
        const layers = this.drawnLayers[b.id] || [];
        const isActive = b.id === this.activeBusId;
        layers.forEach((l) => {
          if (l.setStyle) {
            // Hide other buses' drawings entirely; show only the active bus's
            l.setStyle({
              opacity: isActive ? 0.85 : 0,
              fillOpacity: isActive ? 0.15 : 0,
              weight: isActive ? 3 : 2,
            });
          }
        });
      });
    },

    // ── Marker click → toggle students for active bus ──
    onMarkerClick(loc) {
      if (!this.activeBusId) {
        this.showSnackbar("Select a bus first", "warning");
        return;
      }
      if (this.isDrawingLine) return;

      if (!this.busStudentMap[this.activeBusId]) {
        this.$set(this.busStudentMap, this.activeBusId, []);
        this.$set(this.busAssignmentSourceMap, this.activeBusId, {});
      }

      const ids = this.busStudentMap[this.activeBusId];
      const sources = this.busAssignmentSourceMap[this.activeBusId];
      const residentIds = loc.residents.map((r) => r.studentID);

      const allSelected = residentIds.every((id) => ids.includes(id));
      if (allSelected) {
        // Deselect all at this location
        residentIds.forEach((id) => {
          const idx = ids.indexOf(id);
          if (idx > -1) ids.splice(idx, 1);
          delete sources[id];
        });
      } else {
        // Select all at this location
        residentIds.forEach((id) => {
          if (!ids.includes(id)) {
            ids.push(id);
            sources[id] = "MANUAL";
          }
        });
      }
      this.$forceUpdate();
    },

    // ── Filter conflict ──
    cancelFilterChange() {
      this.filterConflictDialog = false;
      this.pendingFilters = null;
    },
    applyFilterChange() {
      this.filterConflictDialog = false;
      this.pendingFilters = null;
    },

    // ── MAP READY ──
    onMapReady(map) {
      this.map = map;
      this.$nextTick(() => {
        if (this.visibleLocations.length) {
          const bounds = L.latLngBounds(
            this.visibleLocations.map((l) => [l.lat, l.lng])
          );
          map.fitBounds(bounds, { padding: [50, 50] });
        }
        this.setupBoxSelection();
        this.setupLineDrawing();
        this.restoreDrawings();
        this.updateDrawingLayerOpacity();
        this.updateMapCursor();
      });
    },

    // ── BOX SELECTION (shift+drag) ──
    setupBoxSelection() {
      const map = this.map;
      map.on("mousedown", (e) => {
        if (!e.originalEvent.shiftKey) return;
        if (this.drawingMode !== "BOX") return; // only in BOX mode
        if (!this.activeBusId) {
          this.showSnackbar("Select a bus first", "warning");
          return;
        }
        if (this.rectangle) map.removeLayer(this.rectangle);
        this.startLatLng = e.latlng;
        const busCol = this.busColorById(this.activeBusId) || "#3388ff";
        this.rectangle = L.rectangle([e.latlng, e.latlng], {
          color: busCol,
          weight: 2,
          fillOpacity: 0.15,
          dashArray: "5 5",
        }).addTo(map);
        map.dragging.disable();
      });
      map.on("mousemove", (e) => {
        if (!this.startLatLng || !this.rectangle) return;
        this.rectangle.setBounds(L.latLngBounds(this.startLatLng, e.latlng));
      });
      map.on("mouseup", () => {
        if (!this.rectangle || !this.startLatLng) return;
        if (this.drawingMode === "BOX") {
          const b = this.rectangle.getBounds();
          this.selectLocationsInBounds(b);
        } else {
          map.removeLayer(this.rectangle);
          this.rectangle = null;
        }
        this.startLatLng = null;
        map.dragging.enable();
      });
    },
    selectLocationsInBounds(bounds) {
      if (!this.activeBusId) {
        this.showSnackbar("Select a bus first", "warning");
        return;
      }
      if (!this.busStudentMap[this.activeBusId]) {
        this.$set(this.busStudentMap, this.activeBusId, []);
        this.$set(this.busAssignmentSourceMap, this.activeBusId, {});
      }
      const ids = this.busStudentMap[this.activeBusId];
      const sources = this.busAssignmentSourceMap[this.activeBusId];
      let added = 0;
      this.visibleLocations.forEach((loc) => {
        if (!bounds.contains([loc.lat, loc.lng])) return;
        loc.residents.forEach((r) => {
          if (!ids.includes(r.studentID)) {
            ids.push(r.studentID);
            sources[r.studentID] = "BOX_DRAW";
            added++;
          }
        });
      });
      // Store the rectangle drawing and track in drawnLayers
      if (this.rectangle) {
        if (added > 0) {
          const geoJson = JSON.stringify(this.rectangle.toGeoJSON());
          if (!this.busDrawingMap[this.activeBusId]) {
            this.$set(this.busDrawingMap, this.activeBusId, []);
          }
          this.busDrawingMap[this.activeBusId].push({
            drawingType: "BOX",
            geoJson,
            selectionRadiusFeet: null,
            createdAt: new Date().toISOString(),
          });
          // Track the live rectangle layer so visibility is managed when switching buses
          if (!this.drawnLayers[this.activeBusId]) {
            this.$set(this.drawnLayers, this.activeBusId, []);
          }
          this.drawnLayers[this.activeBusId].push(this.rectangle);
        } else {
          // No students selected — remove the rectangle immediately
          this.map.removeLayer(this.rectangle);
        }
        this.rectangle = null;
      }
      this.$forceUpdate();
      if (added)
        this.showSnackbar(
          added + " student(s) added to " + this.activeBusNumber
        );
    },

    // ── LINE DRAWING ──
    setupLineDrawing() {
      const map = this.map;

      // Debounce single click to avoid dblclick firing 2 click events
      map.on("click", (e) => {
        if (this.drawingMode !== "LINE" && this.drawingMode !== "RADIUS_LINE")
          return;
        if (!this.activeBusId) {
          this.showSnackbar("Select a bus first", "warning");
          return;
        }
        if (e.originalEvent.shiftKey) return;

        clearTimeout(this._clickTimer);
        this._clickTimer = setTimeout(() => {
          if (!this.isDrawingLine) {
            map.doubleClickZoom.disable();
          }
          this.isDrawingLine = true;
          this.drawingPoints.push(e.latlng);

          if (this.currentPolyline) {
            this.currentPolyline.setLatLngs(this.drawingPoints);
          } else {
            this.currentPolyline = L.polyline(this.drawingPoints, {
              color: this.activeBusColor,
              weight: 3,
              opacity: 0.85,
              dashArray: "6 4",
            }).addTo(map);
          }
        }, 200);
      });

      map.on("mousemove", (e) => {
        if (!this.isDrawingLine || !this.drawingPoints.length) return;
        const pts = [...this.drawingPoints, e.latlng];
        if (this.previewPolyline) {
          this.previewPolyline.setLatLngs(pts);
        } else {
          this.previewPolyline = L.polyline(pts, {
            color: this.activeBusColor,
            weight: 2,
            opacity: 0.5,
            dashArray: "4 4",
          }).addTo(map);
        }
      });

      map.on("dblclick", (e) => {
        clearTimeout(this._clickTimer); // prevent the pending single-click from firing
        if (this.drawingMode !== "LINE" && this.drawingMode !== "RADIUS_LINE")
          return;
        if (!this.isDrawingLine) return;
        e.originalEvent.preventDefault();
        e.originalEvent.stopPropagation();
        this.finishLineDrawing();
        map.doubleClickZoom.enable();
      });
    },
    finishLineDrawing() {
      if (!this.isDrawingLine || this.drawingPoints.length < 2) {
        this.cancelDrawing();
        return;
      }

      const radiusMeters =
        this.drawingMode === "RADIUS_LINE"
          ? (this.radiusFeet || 500) * FEET_TO_METERS
          : LINE_DEFAULT_RADIUS_FEET * FEET_TO_METERS;

      if (!this.busStudentMap[this.activeBusId]) {
        this.$set(this.busStudentMap, this.activeBusId, []);
        this.$set(this.busAssignmentSourceMap, this.activeBusId, {});
      }
      const ids = this.busStudentMap[this.activeBusId];
      const sources = this.busAssignmentSourceMap[this.activeBusId];
      let added = 0;

      this.visibleLocations.forEach((loc) => {
        const dist = distanceToPolyline(loc.lat, loc.lng, this.drawingPoints);
        if (dist <= radiusMeters) {
          loc.residents.forEach((r) => {
            if (!ids.includes(r.studentID)) {
              ids.push(r.studentID);
              sources[r.studentID] =
                this.drawingMode === "RADIUS_LINE"
                  ? "RADIUS_DRAW"
                  : "LINE_DRAW";
              added++;
            }
          });
        }
      });

      // Store drawing
      const geoJson = JSON.stringify({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: this.drawingPoints.map((p) => [p.lng, p.lat]),
        },
      });
      if (!this.busDrawingMap[this.activeBusId]) {
        this.$set(this.busDrawingMap, this.activeBusId, []);
      }
      this.busDrawingMap[this.activeBusId].push({
        drawingType: this.drawingMode,
        geoJson,
        selectionRadiusFeet:
          this.drawingMode === "RADIUS_LINE" ? this.radiusFeet : null,
        createdAt: new Date().toISOString(),
      });

      // Keep polyline on map (solid)
      if (this.currentPolyline) {
        this.currentPolyline.setStyle({
          dashArray: null,
          opacity: 0.85,
          weight: 3,
        });
        if (!this.drawnLayers[this.activeBusId]) {
          this.drawnLayers[this.activeBusId] = [];
        }
        this.drawnLayers[this.activeBusId].push(this.currentPolyline);
      }

      this.cancelDrawing(false);
      this.updateDrawingLayerOpacity();
      this.$forceUpdate();
      if (added) {
        this.showSnackbar(
          added + " student(s) added to " + this.activeBusNumber
        );
      } else {
        this.showSnackbar("No students found near this line", "warning");
      }
    },
    cancelDrawing(removePolyline) {
      clearTimeout(this._clickTimer);
      if (removePolyline !== false && this.currentPolyline && this.map) {
        this.map.removeLayer(this.currentPolyline);
      }
      if (this.previewPolyline && this.map) {
        this.map.removeLayer(this.previewPolyline);
        this.previewPolyline = null;
      }
      this.currentPolyline = null;
      this.drawingPoints = [];
      this.isDrawingLine = false;
      if (this.map) {
        this.map.doubleClickZoom.enable();
        this.updateMapCursor();
      }
    },
    clearAllSelections() {
      if (!this.activeBusId) return;
      this.$set(this.busStudentMap, this.activeBusId, []);
      this.$set(this.busAssignmentSourceMap, this.activeBusId, {});

      // Remove drawn layers for this bus
      if (this.drawnLayers[this.activeBusId] && this.map) {
        this.drawnLayers[this.activeBusId].forEach((l) =>
          this.map.removeLayer(l)
        );
        delete this.drawnLayers[this.activeBusId];
      }
      this.$set(this.busDrawingMap, this.activeBusId, []);
      this.$forceUpdate();
    },

    // ── Restore drawings when loading saved route ──
    restoreDrawings() {
      if (!this.map) return;
      this.buses.forEach((bus, i) => {
        const drawings = this.busDrawingMap[bus.id] || [];
        drawings.forEach((d) => {
          try {
            const geo = JSON.parse(d.geoJson);
            const color = BUS_COLORS[i % BUS_COLORS.length];
            if (d.drawingType === "BOX") {
              const layer = L.geoJSON(geo, {
                style: { color, weight: 2, fillOpacity: 0.1 },
              }).addTo(this.map);
              if (!this.drawnLayers[bus.id]) this.drawnLayers[bus.id] = [];
              this.drawnLayers[bus.id].push(layer);
            } else {
              const coords = geo.geometry && geo.geometry.coordinates;
              if (coords) {
                const latlngs = coords.map((c) => [c[1], c[0]]);
                const layer = L.polyline(latlngs, {
                  color,
                  weight: 3,
                  opacity: 0.7,
                }).addTo(this.map);
                if (!this.drawnLayers[bus.id]) this.drawnLayers[bus.id] = [];
                this.drawnLayers[bus.id].push(layer);
              }
            }
          } catch (err) {
            console.error("Failed to restore drawing", err);
          }
        });
      });
    },

    // ── Add Bus ──
    openAddBusDialog() {
      this.newBusNumber = "";
      this.addBusDialog = true;
    },
    async doAddBus() {
      this.addingBus = true;
      try {
        const nextNum = this.buses.length + 1;
        const busNumber =
          (this.newBusNumber && this.newBusNumber.trim()) || "Bus " + nextNum;
        const payload = {
          id: 0,
          routeDesignID: parseInt(this.$route.params.id),
          busNumber,
          busID: null,
          sortOrder: this.buses.length,
          isCompleted: false,
          notes: null,
          createdAt: new Date().toISOString(),
        };
        const res = await this.$http.post("api/routeDesign/saveBus", payload);
        const newId = res && res.id;
        const newBus = {
          ...payload,
          id: newId,
          _colorIndex: this.buses.length,
        };
        this.buses.push(newBus);
        this.$set(this.busStudentMap, newId, []);
        this.$set(this.busAssignmentSourceMap, newId, {});
        this.activeBusId = newId;
        this.addBusDialog = false;
        this.showSnackbar('Bus "' + busNumber + '" added');
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to add bus", "error");
      } finally {
        this.addingBus = false;
      }
    },

    // ── Rename Bus ──
    editBusNumber(bus) {
      this.renameBusTarget = bus;
      this.renameBusNumber = bus.busNumber;
      this.renameBusDialog = true;
    },
    async doRenameBus() {
      if (!this.renameBusTarget || !this.renameBusNumber.trim()) return;
      this.renamingBus = true;
      try {
        const updated = {
          ...this.renameBusTarget,
          busNumber: this.renameBusNumber.trim(),
        };
        await this.$http.post("api/routeDesign/saveBus", updated);
        const idx = this.buses.findIndex(
          (b) => b.id === this.renameBusTarget.id
        );
        if (idx > -1) this.buses[idx].busNumber = this.renameBusNumber.trim();
        this.renameBusDialog = false;
        this.$forceUpdate();
        this.showSnackbar("Bus renamed");
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to rename bus", "error");
      } finally {
        this.renamingBus = false;
      }
    },

    // ── Delete Bus ──
    confirmDeleteBus(bus) {
      this.deleteBusTarget = bus;
      this.deleteBusDialog = true;
    },
    async doDeleteBus() {
      if (!this.deleteBusTarget) return;
      this.deletingBus = true;
      try {
        await this.$http.post(
          "api/routeDesign/deleteBus",
          this.deleteBusTarget.id
        );
        const id = this.deleteBusTarget.id;
        this.buses = this.buses.filter((b) => b.id !== id);

        // Remove drawn layers
        if (this.drawnLayers[id] && this.map) {
          this.drawnLayers[id].forEach((l) => this.map.removeLayer(l));
          delete this.drawnLayers[id];
        }
        delete this.busStudentMap[id];
        delete this.busAssignmentSourceMap[id];
        delete this.busDrawingMap[id];

        if (this.activeBusId === id) {
          const first = this.buses.find((b) => !b.isCompleted);
          this.activeBusId = first
            ? first.id
            : this.buses.length
            ? this.buses[0].id
            : null;
        }
        this.deleteBusDialog = false;
        this.showSnackbar("Bus deleted");
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to delete bus", "error");
      } finally {
        this.deletingBus = false;
      }
    },

    // ── Save Bus ──
    async saveBus() {
      if (!this.activeBusId) return;
      this.savingBus = true;
      try {
        const bus = this.buses.find((b) => b.id === this.activeBusId);
        if (!bus) return;

        // Save students
        const studentIds = this.busStudentMap[this.activeBusId] || [];
        const sources = this.busAssignmentSourceMap[this.activeBusId] || {};
        const studentPayload = {
          routeDesignBusID: this.activeBusId,
          students: studentIds.map((sId) => ({
            id: 0,
            routeDesignBusID: this.activeBusId,
            studentID: sId,
            assignmentSource: sources[sId] || "MANUAL",
            createdAt: new Date().toISOString(),
          })),
        };
        await this.$http.post(
          "api/routeDesign/saveBusStudents",
          studentPayload
        );

        // Save drawings
        const drawings = this.busDrawingMap[this.activeBusId] || [];
        const drawingPayload = {
          routeDesignBusID: this.activeBusId,
          drawings: drawings.map((d) => ({
            id: 0,
            routeDesignBusID: this.activeBusId,
            drawingType: d.drawingType,
            geoJson: d.geoJson,
            selectionRadiusFeet: d.selectionRadiusFeet || null,
            createdAt: d.createdAt || new Date().toISOString(),
          })),
        };
        await this.$http.post(
          "api/routeDesign/saveBusDrawings",
          drawingPayload
        );

        // Mark bus complete
        const updatedBus = { ...bus, isCompleted: true };
        await this.$http.post("api/routeDesign/saveBus", updatedBus);
        bus.isCompleted = true;
        this.$forceUpdate();

        // Move to next incomplete bus
        const nextBus = this.buses.find(
          (b) => !b.isCompleted && b.id !== this.activeBusId
        );
        if (nextBus) {
          this.activeBusId = nextBus.id;
          this.$nextTick(() => this.updateDrawingLayerOpacity());
          this.showSnackbar("Bus saved! Now working on " + nextBus.busNumber);
        } else {
          this.showSnackbar("Bus saved! All buses are complete.");
          this.activeBusId = null;
          this.$nextTick(() => this.updateDrawingLayerOpacity());
        }
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to save bus", "error");
      } finally {
        this.savingBus = false;
      }
    },

    // ── Final Save ──
    saveFinal() {
      this.finalStatus =
        this.completedBusCount === this.buses.length ? "Complete" : "Partial";
      this.finalSaveDialog = true;
    },
    async doFinalSave() {
      this.savingFinal = true;
      try {
        const payload = {
          ...this.routeDesign,
          status: this.finalStatus,
          updatedAt: new Date().toISOString(),
        };
        await this.$http.post("api/routeDesign/saveRouteDesign", payload);
        this.routeDesign.status = this.finalStatus;
        this.finalSaveDialog = false;
        this.showSnackbar('Route design saved as "' + this.finalStatus + '"');
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to save route design", "error");
      } finally {
        this.savingFinal = false;
      }
    },

    removeStudent(studentID) {
      if (!this.activeBusId) return;
      const ids = this.busStudentMap[this.activeBusId];
      if (!ids) return;
      const idx = ids.indexOf(studentID);
      if (idx > -1) ids.splice(idx, 1);
      const sources = this.busAssignmentSourceMap[this.activeBusId];
      if (sources) delete sources[studentID];
      this.$forceUpdate();
    },
    exportCSV() {
      const BOM = "\uFEFF";
      const routeName = (this.routeDesign && this.routeDesign.name) || "";
      const seasonName =
        (this.routeDesign && this.routeDesign.seasonName) || "";
      const rows = [];

      this.buses.forEach((bus) => {
        const studentIds = this.busStudentMap[bus.id] || [];
        const sources = this.busAssignmentSourceMap[bus.id] || {};
        studentIds.forEach((sId) => {
          const s = this.students.find((st) => st.studentID === sId);
          if (!s) return;
          rows.push(
            [
              '"' + routeName.replace(/"/g, '""') + '"',
              '"' + seasonName.replace(/"/g, '""') + '"',
              '"' + bus.busNumber.replace(/"/g, '""') + '"',
              '="' + (s.studentID || "") + '"',
              '"' +
                ((s.hebName || "") + " " + (s.lastNameH || ""))
                  .trim()
                  .replace(/"/g, '""') +
                '"',
              '"' + (s.engName || "").replace(/"/g, '""') + '"',
              '"' + (s.class || "") + '"',
              '"' + (s.section || "") + '"',
              '"' + (s.building || "") + '"',
              '"' + (s.branch || "") + '"',
              '"' + (s.busAddress1 || "") + '"',
              '"' + (sources[sId] || "") + '"',
            ].join(",")
          );
        });
      });

      if (!rows.length) {
        this.showSnackbar("No students assigned yet", "warning");
        return;
      }

      const headers = [
        "Route Name",
        "Season",
        "Bus Number",
        "Student ID",
        "Hebrew Name",
        "English Name",
        "Class",
        "Section",
        "Building",
        "Branch",
        "Bus Address",
        "Assignment Source",
      ].join(",");

      const csv = BOM + headers + "\n" + rows.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        (routeName || "route-design") + "_students.csv"
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    updateMapCursor() {
      if (!this.map) return;
      const c = this.map.getContainer();
      if (
        this.activeBusId &&
        (this.drawingMode === "LINE" || this.drawingMode === "RADIUS_LINE")
      ) {
        c.style.cursor = "crosshair";
      } else {
        c.style.cursor = "";
      }
    },
    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color || "success";
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.route-design-map-page {
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.fill-height {
  flex: 1;
  overflow: hidden;
}

/* Sidebar */
.sidebar {
  width: 320px;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  overflow: hidden;
  z-index: 1;
}
.sidebar-header {
  flex-shrink: 0;
  background: #388e3c; /* green darken-2 */
}
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 5px;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 9px 4px 6px;
  flex-shrink: 0;
  transition: background 0.15s;
  letter-spacing: 0.2px;
}
.back-btn:hover {
  background: rgba(0, 0, 0, 0.32);
}
.status-pill {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  letter-spacing: 0.3px;
}
.status-pill--draft {
  background: #fff;
  color: #388e3c;
  border: 1.5px solid rgba(255, 255, 255, 0.8);
}
.status-pill--partial {
  background: #ff9800;
  color: #fff;
  border: 1.5px solid rgba(255, 255, 255, 0.5);
}
.status-pill--complete {
  background: #1b5e20;
  color: #a5d6a7;
  border: 1.5px solid #a5d6a7;
}
.header-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}
.sidebar-buses {
  flex: 1;
  min-height: 100px;
  overflow-y: auto;
}
.sidebar-footer {
  flex-shrink: 0;
}

.bus-card {
  cursor: pointer;
  border-radius: 6px !important;
  transition: background 0.15s;
}
.bus-card:hover {
  background: #f5f5f5;
}
.bus-card--active {
  background: #f0fff4 !important;
}
.bus-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  transition: background 0.15s;
}
.bus-action-btn:hover {
  background: rgba(0, 0, 0, 0.07);
}
.bus-color-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  min-width: 14px;
  max-width: 14px;
  min-height: 14px;
  max-height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  align-self: center;
  border: 2px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* Drawing mode toggle (CustomPayment-style) */
.draw-toggle-group {
  display: inline-flex;
  border: 1px solid #c8e6c9;
  border-radius: 6px;
  overflow: hidden;
}
.draw-toggle-group--disabled {
  opacity: 0.4;
  pointer-events: none;
}
.radius-inline-input {
  display: inline-flex;
  align-items: center;
  border: 1px solid #c8e6c9;
  border-radius: 6px;
  overflow: hidden;
  height: 32px;
  background: #fff;
}
.radius-inline-input__field {
  width: 54px;
  border: none;
  outline: none;
  padding: 0 6px;
  font-size: 13px;
  font-weight: 500;
  color: #2e7d32;
  text-align: right;
  background: transparent;
  -moz-appearance: textfield;
}
.radius-inline-input__field::-webkit-outer-spin-button,
.radius-inline-input__field::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.radius-inline-input__unit {
  padding: 0 8px 0 2px;
  font-size: 12px;
  color: #888;
  background: #f1f8e9;
  height: 100%;
  display: flex;
  align-items: center;
  border-left: 1px solid #c8e6c9;
}
.draw-toggle-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  background: #fff;
  color: #555;
  border: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  outline: none;
  white-space: nowrap;
}
.draw-toggle-btn + .draw-toggle-btn {
  border-left: 1px solid #c8e6c9;
}
.draw-toggle-btn:hover {
  background: #f1f8e9;
  color: #2e7d32;
}
.draw-toggle-btn--active {
  background: #4caf50 !important;
  color: #fff !important;
}
.draw-toggle-btn--active .v-icon {
  color: #fff !important;
}

/* Selected students panel */
.sidebar-students {
  flex-shrink: 0;
  border-top: 1px solid #e0e0e0;
  max-height: 280px;
  display: flex;
  flex-direction: column;
}
.selected-students-header {
  flex-shrink: 0;
}
.selected-students-list {
  overflow-y: auto;
  flex: 1;
}
.student-row {
  border-bottom: 1px solid #f0f0f0;
}
.student-row:hover {
  background: #fafafa;
}
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Map area */
.map-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.map-toolbar {
  flex-shrink: 0;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  min-height: 52px;
}
.filter-panel {
  flex-shrink: 0;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
}
.instruction-strip {
  background: #f1f8e9;
  border-bottom: 1px solid #c8e6c9;
  color: #444;
}
.instruction-strip__row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px 4px;
}
.instruction-strip__row--active {
  background: #e8f5e9;
}
.instruction-strip__icon {
  margin-top: 1px;
  flex-shrink: 0;
}
.instruction-strip__main {
  font-size: 12px;
  font-weight: 600;
  color: #2e7d32;
  line-height: 1.3;
}
.instruction-strip__sub {
  font-size: 11px;
  color: #555;
  line-height: 1.4;
  margin-top: 1px;
}
.instruction-strip__tip {
  padding: 2px 12px 7px 36px;
  font-size: 11px;
  color: #888;
  line-height: 1.4;
}
.instruction-strip kbd {
  background: #e8e8e8;
  border: 1px solid #b0b0b0;
  border-bottom-width: 2px;
  border-radius: 3px;
  padding: 1px 5px;
  font-size: 0.78em;
  font-family: monospace;
  color: #333;
  line-height: 1.4;
  display: inline-block;
}
.map-container {
  flex: 1;
  position: relative;
  z-index: 0; /* isolates Leaflet pane z-indexes so Vuetify dialogs appear above */
  overflow: hidden;
}
.the-map {
  width: 100%;
  height: 100%;
}
.custom-circle-marker {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}
</style>
